import { useState } from 'react'
import api from '../../services/milk-api'

interface RequestAccessModalProps {
  onClose: () => void
}

interface FormData {
  dairyName: string
  ownerName: string
  mobile: string
  email: string
  city: string
  subscriberCount: string
  preferredPlan: string
  message: string
}

interface FormErrors {
  [key: string]: string
}

export default function RequestAccessModal({ onClose }: RequestAccessModalProps) {
  const [form, setForm] = useState<FormData>({
    dairyName: '',
    ownerName: '',
    mobile: '',
    email: '',
    city: '',
    subscriberCount: '',
    preferredPlan: 'monthly',
    message: '',
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const validate = (): boolean => {
    const e: FormErrors = {}
    if (!form.dairyName.trim()) e.dairyName = 'Dairy / Business name is required'
    if (!form.ownerName.trim()) e.ownerName = 'Owner name is required'
    if (!form.mobile.trim()) e.mobile = 'Mobile number is required'
    else if (!/^\d{10}$/.test(form.mobile.trim())) e.mobile = 'Enter a valid 10-digit number'
    if (!form.email.trim()) e.email = 'Email is required'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = 'Enter a valid email'
    if (!form.city.trim()) e.city = 'City is required'
    if (!form.subscriberCount.trim()) e.subscriberCount = 'Number of subscribers is required'
    else if (isNaN(Number(form.subscriberCount)) || Number(form.subscriberCount) < 0) e.subscriberCount = 'Enter a valid number'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    setLoading(true)
    try {
      await api.createTenantRequest({
        dairyName: form.dairyName.trim(),
        ownerName: form.ownerName.trim(),
        mobile: form.mobile.trim(),
        email: form.email.trim(),
        city: form.city.trim(),
        subscriberCount: Number(form.subscriberCount),
        preferredPlan: form.preferredPlan,
        message: form.message.trim() || undefined,
      })
      setSubmitted(true)
    } catch {
      setErrors({ submit: 'Something went wrong. Please try again.' })
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (field: keyof FormData, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose()
  }

  if (submitted) {
    return (
      <div className="mf-modal-overlay" onClick={handleOverlayClick}>
        <div className="mf-modal">
          <div className="mf-modal-success">
            <div className="success-icon">✓</div>
            <h3>Request Submitted Successfully</h3>
            <p>
              Thank you. Your request has been received. Our team will review it
              and contact you shortly.
            </p>
            <button
              className="mf-btn-pricing primary"
              style={{ marginTop: 24, width: 'auto', padding: '12px 32px' }}
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="mf-modal-overlay" onClick={handleOverlayClick}>
      <div className="mf-modal">
        <div className="mf-modal-header">
          <h2>Request Access to MilkFlow</h2>
          <button className="mf-modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <div className="mf-modal-body">
          <form onSubmit={handleSubmit} noValidate>
            <div className="mf-form-group">
              <label htmlFor="dairyName">Dairy / Business Name</label>
              <input
                id="dairyName"
                type="text"
                className={errors.dairyName ? 'error' : ''}
                value={form.dairyName}
                onChange={e => handleChange('dairyName', e.target.value)}
                placeholder="e.g. Sunrise Dairy"
              />
              {errors.dairyName && <div className="mf-form-error">{errors.dairyName}</div>}
            </div>

            <div className="mf-form-group">
              <label htmlFor="ownerName">Owner Name</label>
              <input
                id="ownerName"
                type="text"
                className={errors.ownerName ? 'error' : ''}
                value={form.ownerName}
                onChange={e => handleChange('ownerName', e.target.value)}
                placeholder="e.g. Rajesh Kumar"
              />
              {errors.ownerName && <div className="mf-form-error">{errors.ownerName}</div>}
            </div>

            <div className="mf-form-row">
              <div className="mf-form-group">
                <label htmlFor="mobile">Mobile Number</label>
                <input
                  id="mobile"
                  type="tel"
                  className={errors.mobile ? 'error' : ''}
                  value={form.mobile}
                  onChange={e => handleChange('mobile', e.target.value)}
                  placeholder="10-digit number"
                  maxLength={10}
                />
                {errors.mobile && <div className="mf-form-error">{errors.mobile}</div>}
              </div>

              <div className="mf-form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  id="email"
                  type="email"
                  className={errors.email ? 'error' : ''}
                  value={form.email}
                  onChange={e => handleChange('email', e.target.value)}
                  placeholder="you@example.com"
                />
                {errors.email && <div className="mf-form-error">{errors.email}</div>}
              </div>
            </div>

            <div className="mf-form-row">
              <div className="mf-form-group">
                <label htmlFor="city">City</label>
                <input
                  id="city"
                  type="text"
                  className={errors.city ? 'error' : ''}
                  value={form.city}
                  onChange={e => handleChange('city', e.target.value)}
                  placeholder="e.g. Pune"
                />
                {errors.city && <div className="mf-form-error">{errors.city}</div>}
              </div>

              <div className="mf-form-group">
                <label htmlFor="subscribers">Number of Subscribers</label>
                <input
                  id="subscribers"
                  type="number"
                  className={errors.subscriberCount ? 'error' : ''}
                  value={form.subscriberCount}
                  onChange={e => handleChange('subscriberCount', e.target.value)}
                  placeholder="e.g. 200"
                  min="0"
                />
                {errors.subscriberCount && <div className="mf-form-error">{errors.subscriberCount}</div>}
              </div>
            </div>

            <div className="mf-form-group">
              <label htmlFor="plan">Preferred Plan</label>
              <select
                id="plan"
                value={form.preferredPlan}
                onChange={e => handleChange('preferredPlan', e.target.value)}
              >
                <option value="monthly">₹999 / Month</option>
                <option value="yearly">₹10,000 / Year (Save ₹1,988)</option>
              </select>
            </div>

            <div className="mf-form-group">
              <label htmlFor="message">Message / Additional Information</label>
              <textarea
                id="message"
                rows={3}
                value={form.message}
                onChange={e => handleChange('message', e.target.value)}
                placeholder="Tell us about your dairy (optional)"
                style={{ resize: 'vertical', minHeight: '70px' }}
              />
            </div>

            {errors.submit && (
              <div className="mf-form-error" style={{ textAlign: 'center', marginBottom: 8 }}>{errors.submit}</div>
            )}

            <button type="submit" className="mf-btn-submit" disabled={loading}>
              {loading && <span className="spinner" />}
              {loading ? 'Submitting...' : 'Request Access'}
            </button>

            <p className="mf-form-note">
              Your request will be reviewed by the MilkFlow team.
            </p>
          </form>
        </div>
      </div>
    </div>
  )
}
